import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard,
  FolderKanban,
  Search,
  Users,
  Link as LinkIcon,
  FileText,
  Code,
  BarChart3,
  TrendingUp,
  Settings,
  Zap,
  Building2,
  Globe,
  Bot,
  Sparkles,
  Plug,
  Plug2,
  MessageSquareQuote,
  Bookmark,
  ClipboardCheck,
  MessageSquare,
} from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { Separator } from '@/components/ui/separator';

// Grouped by WHOSE data it is, not by which agent produced it.
//
// The old grouping ("AI Agents") was an implementation detail: a user asking
// "how is my site doing?" had to know that rank tracking and technical SEO were
// built as agents while PageSpeed was not. These four groups map to questions
// people actually ask — what does the market look like, how is my site doing,
// what can this thing produce for me, and what is plugged in.
const getNavigation = (t: (key: string) => string) => [
  { name: t('navigation.dashboard'), href: '/app', icon: LayoutDashboard },
  { name: t('navigation.companyProfile'), href: '/app/company-profile', icon: Building2 },
  {
    // Other people's data: the market, the competition, the answer engines.
    name: t('navigation.groupMarket'),
    items: [
      { name: t('navigation.keywordResearch'), href: '/app/keyword-research', icon: Search },
      { name: t('navigation.competitorAnalysis'), href: '/app/competitor-analysis', icon: Users },
      { name: t('navigation.backlinkAnalysis'), href: '/app/backlink-analysis', icon: LinkIcon },
      { name: t('navigation.aiVisibility'), href: '/app/ai-visibility', icon: Bot },
      { name: t('navigation.promptExplorer'), href: '/app/prompt-explorer', icon: MessageSquareQuote },
      { name: t('navigation.crossDomainAnalysis'), href: '/app/cross-domain', icon: Globe },
    ],
  },
  {
    // The client's own property — first-party data plus what we measure on it.
    name: t('navigation.groupOwnSite'),
    items: [
      { name: t('navigation.rankTracking'), href: '/app/rank-tracking', icon: TrendingUp },
      { name: t('navigation.savedKeywords'), href: '/app/saved-keywords', icon: Bookmark },
      { name: t('navigation.serpTracker'), href: '/app/serp-tracker', icon: BarChart3 },
      { name: t('navigation.pagespeed'), href: '/app/pagespeed', icon: Zap },
      { name: t('navigation.siteAudit'), href: '/app/site-audit', icon: ClipboardCheck },
      { name: t('navigation.technicalSeo'), href: '/app/technical-seo', icon: Code },
    ],
  },
  {
    // What the workbench PRODUCES. This is the wedge — audit-only tools stop at
    // the two groups above.
    name: t('navigation.groupExecution'),
    items: [
      { name: 'Skills', href: '/app/skills', icon: Sparkles },
      { name: t('navigation.contentWriter'), href: '/app/content-writer', icon: FileText },
      { name: 'Kopilot', href: '/app/chat', icon: MessageSquare },
    ],
  },
  {
    name: t('navigation.groupConnect'),
    items: [
      { name: t('navigation.integrations'), href: '/app/integrations', icon: Plug2 },
      { name: t('navigation.aiMcp'), href: '/app/ai-mcp', icon: Plug },
      { name: t('navigation.projects'), href: '/app/projects', icon: FolderKanban },
    ],
  },
];

interface SidebarProps {
  isOpen: boolean;
}

export function Sidebar({ isOpen }: SidebarProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigation = getNavigation(t);

  return (
    <aside
      className={cn(
        'fixed left-0 top-0 z-40 h-screen w-64 border-r bg-card transition-transform',
        !isOpen && '-translate-x-full'
      )}
    >
      <div className="flex h-full flex-col">
        {/* Logo */}
        <div className="flex h-16 items-center px-6">
          <h1 className="text-xl font-semibold">SEO Platform</h1>
        </div>

        <Separator />

        {/* Navigation */}
        <nav className="flex-1 space-y-1 px-4 py-4 overflow-y-auto">
          {navigation.map((item) =>
            'items' in item ? (
              <div key={item.name} className="space-y-1">
                <div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  {item.name}
                </div>
                {item.items?.map((subItem) => {
                  const isActive = location.pathname === subItem.href;
                  return (
                    <Link
                      key={subItem.href}
                      to={subItem.href}
                      className={cn(
                        'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                        isActive
                          ? 'bg-accent text-accent-foreground'
                          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                      )}
                    >
                      <subItem.icon size={18} />
                      <span>{subItem.name}</span>
                    </Link>
                  );
                })}
              </div>
            ) : (
              <Link
                key={item.href}
                to={item.href}
                className={cn(
                  'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                  location.pathname === item.href
                    ? 'bg-accent text-accent-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                )}
              >
                <item.icon size={18} />
                <span>{item.name}</span>
              </Link>
            )
          )}

          <Separator className="my-4" />

          <Link
            to="/app/settings"
            className={cn(
              'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
              location.pathname === '/app/settings'
                ? 'bg-accent text-accent-foreground'
                : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
            )}
          >
            <Settings size={18} />
            <span>{t('navigation.settings')}</span>
          </Link>
        </nav>
      </div>
    </aside>
  );
}
